import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Alert,
  Avatar,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Cancel as CancelIcon,
  Refresh as RetryIcon,
  Delete as RemoveIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  CloudUpload as UploadingIcon,
  AutoFixHigh as ProcessingIcon,
  Done as ReadyIcon
} from '@mui/icons-material';
import { en } from '../translations/en';

export interface UploadProgress {
  fileId: string;
  fileName: string;
  fileSize: number;
  uploadedBytes: number;
  totalChunks: number;
  completedChunks: number;
  uploadSpeed: number; // bytes per second
  estimatedTimeRemaining: number; // seconds
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled' | 'processing' | 'ready';
  error?: string;
  thumbnailUrl?: string;
}

interface UploadProgressProps {
  progress: UploadProgress;
  onCancel: (fileId: string) => void;
  onRetry: (fileId: string) => void;
  onRemove: (fileId: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const formatSpeed = (bytesPerSecond: number): string => {
  if (bytesPerSecond >= 1024 * 1024) {
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  }
  if (bytesPerSecond >= 1024) {
    return `${Math.round(bytesPerSecond / 1024)} KB/s`;
  }
  return `${bytesPerSecond} B/s`;
};

const formatTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s ${en.uploadProgress.remaining}`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s ${en.uploadProgress.remaining}`;
};

const getStatusIcon = (status: UploadProgress['status']) => {
  switch (status) {
    case 'pending':
      return <PendingIcon color="action" />;
    case 'uploading':
      return <UploadingIcon color="primary" />;
    case 'completed':
      return <CompletedIcon color="success" />;
    case 'processing':
      return <ProcessingIcon color="primary" />;
    case 'ready':
      return <ReadyIcon color="success" />;
    case 'failed':
      return <ErrorIcon color="error" />;
    case 'cancelled':
      return <CancelIcon color="action" />;
    default:
      return <PendingIcon color="action" />;
  }
};

const getStatusColor = (status: UploadProgress['status']) => {
  switch (status) {
    case 'pending':
      return 'default';
    case 'uploading':
      return 'primary';
    case 'completed':
      return 'success';
    case 'processing':
      return 'primary';
    case 'ready':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusText = (status: UploadProgress['status']) => {
  switch (status) {
    case 'pending':
      return en.uploadProgress.pending;
    case 'uploading':
      return en.uploadProgress.uploading;
    case 'completed':
      return en.uploadProgress.completed;
    case 'processing':
      return en.uploadProgress.processing;
    case 'ready':
      return en.uploadProgress.ready;
    case 'failed':
      return en.uploadProgress.failed;
    case 'cancelled':
      return en.uploadProgress.cancelled;
    default:
      return en.uploadProgress.unknown;
  }
};

const UploadProgressComponent: React.FC<UploadProgressProps> = ({
  progress,
  onCancel,
  onRetry,
  onRemove
}) => {
  const theme = useTheme();
  const percentage = progress.fileSize > 0
    ? Math.round((progress.uploadedBytes / progress.fileSize) * 100)
    : 0;

  const showCancel = progress.status === 'uploading' || progress.status === 'pending';
  const showRetry = progress.status === 'failed';
  const showRemove = progress.status === 'ready' || progress.status === 'cancelled';
  // Hide the bar in terminal states (issue #1098). `completed` is intentionally
  // NOT terminal: bytes are uploaded but server-side renditions may still be
  // processing — `ready` is the true done state (see UploadQueue.tsx getQueueStats).
  const showProgressBar =
    progress.status !== 'ready' &&
    progress.status !== 'cancelled' &&
    progress.status !== 'failed';

  return (
    <Card variant="outlined" sx={{ mb: 1, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {progress.thumbnailUrl && (progress.status === 'ready' || progress.status === 'completed') ? (
              <Avatar
                src={progress.thumbnailUrl}
                alt={en.uploadProgress.thumbnailPreview}
                variant="rounded"
                sx={{
                  width: 48,
                  height: 48,
                  border: `1px solid ${theme.palette.divider}`,
                  '& img': { objectFit: 'contain' }
                }}
              />
            ) : (
              <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: theme.palette.background.default }}>
                {getStatusIcon(progress.status)}
              </Avatar>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={progress.fileName}>
                {progress.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(progress.fileSize)}
              </Typography>
            </Box>

            <Chip
              icon={getStatusIcon(progress.status)}
              label={getStatusText(progress.status)}
              color={getStatusColor(progress.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
              size="small"
            />
          </Box>

          {showProgressBar && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {percentage}%
                </Typography>
                {progress.status === 'uploading' && (
                  <Typography variant="body2" color="text.secondary">
                    {formatSpeed(progress.uploadSpeed)}
                  </Typography>
                )}
              </Box>

              <LinearProgress
                variant="determinate"
                value={percentage}
                color="primary"
                role="progressbar"
                aria-label={`${en.uploadProgress.uploadProgressFor} ${progress.fileName}`}
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200]
                }}
              />

              {progress.status === 'uploading' && progress.estimatedTimeRemaining > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {formatTime(progress.estimatedTimeRemaining)}
                </Typography>
              )}
            </Box>
          )}

          {progress.error && progress.status === 'failed' && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              <Typography variant="body2">
                {progress.error}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {showCancel && (
              <IconButton
                size="small"
                onClick={() => onCancel(progress.fileId)}
                aria-label={en.uploadProgress.cancelUpload}
                color="default"
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <CancelIcon />
              </IconButton>
            )}

            {showRetry && (
              <IconButton
                size="small"
                onClick={() => onRetry(progress.fileId)}
                aria-label={en.uploadProgress.retryUpload}
                color="primary"
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '20'
                  }
                }}
              >
                <RetryIcon />
              </IconButton>
            )}

            {showRemove && (
              <IconButton
                size="small"
                onClick={() => onRemove(progress.fileId)}
                aria-label={en.uploadProgress.removeFromList}
                color="default"
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover
                  }
                }}
              >
                <RemoveIcon />
              </IconButton>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export { UploadProgressComponent as UploadProgress };
